import React, { useEffect, useState } from "react";
import { Download, Archive, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "@/lib/api";

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/audit").then(({ data }) => setLogs(data));
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    window.open(`${API_BASE}/admin/audit/export.csv`, "_blank");
  };

  const archive = async () => {
    const days = window.prompt("Archive audit entries older than how many days?", "90");
    if (!days) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/audit/archive?older_than_days=${parseInt(days, 10)}`);
      toast.success(`Archived ${data.archived} records older than ${data.older_than_days} days`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not archive");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    const phrase = window.prompt(
      "This will PERMANENTLY delete every audit log entry. Type the phrase exactly to confirm:\n\nI understand this is irreversible"
    );
    if (phrase !== "I understand this is irreversible") {
      if (phrase !== null) toast.error("Confirmation phrase did not match. Nothing was cleared.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/audit?confirm=${encodeURIComponent(phrase)}`);
      toast.success(`Cleared ${data.cleared} records`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not clear");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-audit">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="overline mb-2">Audit log</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Recent activity</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="btn-ghost-ink !py-2" data-testid="audit-export"><Download size={14} weight="bold" /> Export CSV</button>
          <button onClick={archive} disabled={busy} className="btn-ghost-ink !py-2 disabled:opacity-50" data-testid="audit-archive"><Archive size={14} weight="bold" /> Archive old</button>
          <button onClick={clear} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid="audit-clear"><Trash size={14} weight="bold" /> Clear all</button>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              {["Timestamp", "User", "Action", "Entity", "Entity ID"].map((h) => (
                <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500 font-mono">No audit entries yet.</td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`audit-row-${l.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-zinc-600">{l.timestamp ? new Date(l.timestamp).toLocaleString() : "—"}</td>
                <td className="px-4 py-3">{l.user_email || "—"}</td>
                <td className="px-4 py-3"><span className="status-pill">{l.action}</span></td>
                <td className="px-4 py-3 text-zinc-700">{l.entity || l.target_type}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{l.entity_id || l.target_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
